/**
 * The FCCERS-3 Profile (manual p. 97), drawn for one site.
 *
 * All 33 items run down the left, banded by subscale; scores 1-7 run across.
 * Each selected observation is a connected line of dots, so two visits laid
 * over each other show item-by-item movement at a glance — which is the whole
 * point of the sheet and something a subscale average cannot express.
 *
 * Hand-drawn SVG rather than a chart library: this is a categorical dot plot
 * with grouping bands and a second stacked panel, which every library either
 * refuses or makes harder to control than plain geometry.
 */

import type { ProfileItem, ProfileObservation } from '@/lib/dashboard/siteProfile';
import { SUBSCALE_META } from '@/lib/dashboard/types';

/** Distinct per observation, readable on both themes, colour-blind safe. */
const SERIES_COLORS = ['#1f2b5b', '#c1553a', '#3f7a4f', '#8a5cc0', '#b8892a'];

const ROW_H = 19;
const BAND_GAP = 9;
const LEFT = 244;
/** Gutter between item labels and the plot, reserved for NA markers. */
const NA_GUTTER = 30;
const RIGHT = 26;
/** Width of the numeric value column drawn to the right of the summary panel. */
const VALUE_COL = 44;
const TOP = 34;
const SUMMARY_GAP = 26;

export function SiteProfileChart({
  items,
  observations,
  width = 860,
}: {
  items: ProfileItem[];
  observations: ProfileObservation[];
  width?: number;
}) {
  const groups = SUBSCALE_META.map((s) => ({
    meta: s,
    items: items.filter((i) => i.subscaleId === s.id),
  })).filter((g) => g.items.length > 0);

  // Lay the item rows out once; everything else positions against this.
  const rowY = new Map<number, number>();
  let y = TOP;
  for (const g of groups) {
    for (const it of g.items) {
      rowY.set(it.number, y);
      y += ROW_H;
    }
    y += BAND_GAP;
  }
  const itemsBottom = y - BAND_GAP;

  const summaryRows = [
    ...SUBSCALE_META.filter((s) => groups.some((g) => g.meta.id === s.id)).map(
      (s) => ({ key: s.id, label: s.name, kind: 'subscale' as const }),
    ),
    { key: 'total', label: 'TOTAL MEAN SCORE', kind: 'total' as const },
  ];
  const summaryTop = itemsBottom + SUMMARY_GAP;
  const height = summaryTop + summaryRows.length * ROW_H + 30;

  // Printing every series' averages needs room; past three it gets cramped, so
  // the numbers drop away and the dots carry it alone.
  const showValues = observations.length <= 3;
  const valueColW = showValues ? VALUE_COL * observations.length : 0;
  const plotW = width - LEFT - RIGHT - valueColW;
  const x = (score: number) => LEFT + ((score - 1) / 6) * plotW;

  const summaryValue = (o: ProfileObservation, key: string) =>
    key === 'total'
      ? o.totalAverage
      : o.subscaleAverages[key as keyof typeof o.subscaleAverages] ?? null;

  return (
    <div className="overflow-x-auto">
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label={`Item-level profile across ${observations.length} observation${observations.length === 1 ? '' : 's'}`}
        className="min-w-[860px]"
      >
        {/* score gridlines */}
        {[1, 2, 3, 4, 5, 6, 7].map((s) => (
          <g key={s}>
            <line
              x1={x(s)}
              y1={TOP - 12}
              x2={x(s)}
              y2={height - 24}
              stroke="currentColor"
              strokeWidth={s === 1 || s === 7 ? 1 : 0.5}
              className="text-border"
            />
            <text
              x={x(s)}
              y={TOP - 18}
              textAnchor="middle"
              className="fill-muted-foreground text-[11px]"
            >
              {s}
            </text>
          </g>
        ))}

        {/* subscale bands + item labels */}
        {groups.map((g) => {
          const first = rowY.get(g.items[0].number) ?? 0;
          const last = rowY.get(g.items[g.items.length - 1].number) ?? 0;
          return (
            <g key={g.meta.id}>
              <rect
                x={LEFT}
                y={first - ROW_H / 2}
                width={plotW}
                height={last - first + ROW_H}
                fill={g.meta.color}
                opacity={0.05}
              />
              <rect
                x={LEFT - 5}
                y={first - ROW_H / 2}
                width={3}
                height={last - first + ROW_H}
                fill={g.meta.color}
                rx={1.5}
              />
              {g.items.map((it) => (
                <text
                  key={it.number}
                  x={LEFT - NA_GUTTER}
                  y={(rowY.get(it.number) ?? 0) + 4}
                  textAnchor="end"
                  className="fill-foreground text-[11px]"
                >
                  {it.number}. {it.name.length > 28 ? `${it.name.slice(0, 27)}…` : it.name}
                </text>
              ))}
            </g>
          );
        })}

        {/* one polyline + dots per observation */}
        {observations.map((o, oi) => {
          const color = SERIES_COLORS[oi % SERIES_COLORS.length];
          const pts = items
            .map((it) => {
              const s = o.scores.get(it.number);
              if (typeof s !== 'number') return null;
              return { x: x(s), y: rowY.get(it.number) ?? 0, n: it.number, s };
            })
            .filter(Boolean) as Array<{ x: number; y: number; n: number; s: number }>;
          return (
            <g key={o.recordId}>
              <polyline
                points={pts.map((p) => `${p.x},${p.y}`).join(' ')}
                fill="none"
                stroke={color}
                strokeWidth={1.4}
                opacity={0.55}
              />
              {pts.map((p) => (
                <circle key={p.n} cx={p.x} cy={p.y} r={4} fill={color}>
                  <title>
                    {`Item ${p.n} · score ${p.s} · ${o.date}`}
                  </title>
                </circle>
              ))}
              {/* NA items get a hollow marker at the axis so they read as
                  deliberately excluded rather than missing data. */}
              {items
                .filter((it) => o.naItems.has(it.number))
                .map((it) => (
                  <g key={`na-${it.number}`}>
                    <circle
                      cx={LEFT - NA_GUTTER + 8 + oi * 10}
                      cy={rowY.get(it.number) ?? 0}
                      r={4}
                      fill="none"
                      stroke={color}
                      strokeWidth={1.2}
                      strokeDasharray="2 1.5"
                    >
                      <title>{`Item ${it.number} · Not Applicable · ${o.date}`}</title>
                    </circle>
                  </g>
                ))}
            </g>
          );
        })}

        {/* averages panel */}
        <rect
          x={0}
          y={summaryTop - SUMMARY_GAP / 2}
          width={width}
          height={summaryRows.length * ROW_H + 10}
          className="fill-muted"
          opacity={0.35}
        />
        <line
          x1={0}
          y1={summaryTop - SUMMARY_GAP / 2}
          x2={width}
          y2={summaryTop - SUMMARY_GAP / 2}
          stroke="currentColor"
          className="text-border"
        />
        {/* the summary gridlines have to be redrawn over the panel fill */}
        {[1, 2, 3, 4, 5, 6, 7].map((sc) => (
          <line
            key={`sg-${sc}`}
            x1={x(sc)}
            y1={summaryTop - SUMMARY_GAP / 2}
            x2={x(sc)}
            y2={summaryTop + summaryRows.length * ROW_H - ROW_H / 2}
            stroke="currentColor"
            strokeWidth={sc === 1 || sc === 7 ? 1 : 0.5}
            className="text-border"
          />
        ))}
        {/* Connect each observation's averages, exactly as the item rows above
            are connected — without it you cannot tell which dot is which visit. */}
        {observations.map((o, oi) => {
          const color = SERIES_COLORS[oi % SERIES_COLORS.length];
          const pts = summaryRows
            .map((row, ri) => {
              const v = summaryValue(o, row.key);
              return v == null ? null : { x: x(v), y: summaryTop + ri * ROW_H };
            })
            .filter(Boolean) as Array<{ x: number; y: number }>;
          return (
            <polyline
              key={`sl-${o.recordId}`}
              points={pts.map((p) => `${p.x},${p.y}`).join(' ')}
              fill="none"
              stroke={color}
              strokeWidth={1.4}
              opacity={0.55}
            />
          );
        })}
        {summaryRows.map((row, ri) => {
          const ry = summaryTop + ri * ROW_H;
          const isTotal = row.kind === 'total';
          return (
            <g key={row.key}>
              {isTotal ? (
                <line
                  x1={LEFT - NA_GUTTER - 130}
                  y1={ry - ROW_H / 2}
                  x2={width - RIGHT}
                  y2={ry - ROW_H / 2}
                  stroke="currentColor"
                  className="text-border"
                />
              ) : null}
              <text
                x={LEFT - NA_GUTTER}
                y={ry + 4}
                textAnchor="end"
                className={
                  isTotal
                    ? 'fill-foreground text-[11px] font-semibold uppercase tracking-wide'
                    : 'fill-muted-foreground text-[11px]'
                }
              >
                {row.label}
              </text>
              {observations.map((o, oi) => {
                const v = summaryValue(o, row.key);
                if (v == null) return null;
                const color = SERIES_COLORS[oi % SERIES_COLORS.length];
                return (
                  <g key={o.recordId}>
                    <circle cx={x(v)} cy={ry} r={isTotal ? 5.5 : 4} fill={color}>
                      <title>{`${row.label} · ${v.toFixed(2)} · ${o.date}`}</title>
                    </circle>
                    {showValues ? (
                      <text
                        x={width - RIGHT - valueColW + oi * VALUE_COL + VALUE_COL / 2}
                        y={ry + 4}
                        textAnchor="middle"
                        fill={color}
                        className={
                          isTotal
                            ? 'text-[11px] font-semibold'
                            : 'text-[11px]'
                        }
                        style={{ fontVariantNumeric: 'tabular-nums' }}
                      >
                        {v.toFixed(2)}
                      </text>
                    ) : null}
                  </g>
                );
              })}
            </g>
          );
        })}
        {/* column header for the printed averages */}
        {showValues
          ? observations.map((o, oi) => (
              <text
                key={`vh-${o.recordId}`}
                x={width - RIGHT - valueColW + oi * VALUE_COL + VALUE_COL / 2}
                y={summaryTop - SUMMARY_GAP / 2 - 5}
                textAnchor="middle"
                fill={SERIES_COLORS[oi % SERIES_COLORS.length]}
                className="text-[9px] font-medium"
              >
                {(o.date || '').slice(5)}
              </text>
            ))
          : null}
      </svg>
    </div>
  );
}

export { SERIES_COLORS };
